import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useApolloClient } from '@apollo/client';

import {
  useViewProcessorAdminQuery,
  useViewProcessorAdminLightQuery,
  ViewProcessorAdminQuery,
  ViewProcessorAdminLightQuery,
  ViewProcessorAdminDocument,
  ViewProcessorAdminLightDocument,
  Maybe,
  ViewProcessorAdminQueryResult,
  ViewProcessorAdminLightQueryResult,
} from 'lib/graphql';
import { useUserState } from './user-state-provider';

/**
 * The data shape surfaced through context.
 *
 * When `loadFullData` is false (default) the provider runs the lightweight
 * query so `processorCapabilityMany` entries will NOT have `cuts`,
 * `primalGroups`, `activeSpecification`, `extras`, or `trim`.
 *
 * When `loadFullData` is true those fields are present.  Apollo's normalised
 * cache merges the two result shapes by `__typename + _id`, so switching from
 * light → full never causes a data loss flicker.
 */
interface SettingsContextValue {
  settings: Maybe<
    ViewProcessorAdminQueryResult | ViewProcessorAdminLightQueryResult
  >;
  refetch: () => Promise<any>;
  freshData: ViewProcessorAdminQuery | ViewProcessorAdminLightQuery | null;
  /** True once the full dataset (cuts / primalGroups) has been loaded */
  isFullDataLoaded: boolean;
}

const SettingsContext = createContext<SettingsContextValue | undefined>(
  undefined,
);

interface SettingsProviderProps {
  children: ReactNode;
  /**
   * When true the provider fetches the full `ViewProcessorAdmin` query which
   * includes `cuts`, `primalGroups`, `activeSpecification`, `extras`, and
   * `trim` on each capability.  Defaults to false (light query) so that pages
   * that don't need cutsheet data get a faster initial load.
   */
  loadFullData?: boolean;
  /**
   * Optional vendor ID to filter settings by a specific vendor.
   * When omitted, the authenticated user's current vendor is used.
   */
  vendorId?: string;
}

/**
 * Provider component that manages processor settings as a singleton.
 *
 * By default it runs the lightweight `ViewProcessorAdminLight` query which
 * omits the heavy capability sub-documents (cuts, primalGroups, etc.) not
 * needed by most pages.  Pass `loadFullData` to switch to the full query for
 * pages that render cutsheets or the capability wizard.
 *
 * Uses Apollo Client directly for initial load to populate cache, then
 * `useQuery` with cache-only to subscribe to cache updates.  This prevents
 * automatic refetching while maintaining cache reactivity.
 *
 * @param root0 - Component props
 * @param root0.children - Child components to render within the provider
 * @param root0.loadFullData - Load cuts / primalGroups / activeSpecification
 * @param root0.vendorId - Optional vendor ID to filter settings for a specific vendor
 * @returns Rendered provider component with settings context
 */
export function SettingsProvider({
  children,
  loadFullData = false,
  vendorId = undefined,
}: SettingsProviderProps) {
  const apolloClient = useApolloClient();
  const { vendor } = useUserState();
  const freshDataRef = useRef<
    ViewProcessorAdminQuery | ViewProcessorAdminLightQuery | null
  >(null);
  // Track which query mode has already been fetched so we don't re-fetch on
  // unrelated renders, but do re-fetch when loadFullData upgrades from false → true.
  const loadedModeRef = useRef<'none' | 'light' | 'full'>('none');

  // When an explicit vendorId prop is supplied it takes precedence over the
  // authenticated user's vendor; both are valid ways to scope the query.
  const effectiveVendorId = vendorId ?? vendor;

  // Reset fetched-mode tracking whenever the target vendor changes so we
  // issue a fresh network request for the new vendor's data.
  const prevVendorIdRef = useRef<string | undefined>(vendorId);
  if (prevVendorIdRef.current !== vendorId) {
    prevVendorIdRef.current = vendorId;
    loadedModeRef.current = 'none';
    freshDataRef.current = null;
  }

  // Pick the right query document based on the prop.
  const queryDocument = loadFullData
    ? ViewProcessorAdminDocument
    : ViewProcessorAdminLightDocument;

  // Build filter variables only when an explicit vendorId is provided.
  // The cache-only hooks must receive the same variables as the network fetch
  // so Apollo can locate the correct normalised cache entry.
  const queryVariables = useMemo(
    () => (vendorId ? { filter: { vendor: vendorId } } : undefined),
    [vendorId],
  );

  // Subscribe to cache-only updates for the full query when full data is
  // requested, otherwise subscribe to the light query.
  const fullQueryResult = useViewProcessorAdminQuery({
    fetchPolicy: 'cache-only',
    skip: !effectiveVendorId || !loadFullData,
    variables: queryVariables,
    notifyOnNetworkStatusChange: false,
    onCompleted: (data) => {
      if (data) freshDataRef.current = JSON.parse(JSON.stringify(data));
    },
  });

  const lightQueryResult = useViewProcessorAdminLightQuery({
    fetchPolicy: 'cache-only',
    skip: !effectiveVendorId || loadFullData,
    variables: queryVariables,
    notifyOnNetworkStatusChange: false,
    onCompleted: (data) => {
      if (data) freshDataRef.current = JSON.parse(JSON.stringify(data));
    },
  });

  // Use whichever result is active.
  const queryResult = loadFullData ? fullQueryResult : lightQueryResult;

  // Initial load — populate the cache via a direct network request so the
  // cache-only subscription above can react to it.  We also re-fetch when
  // loadFullData upgrades from false → true so the richer data is available.
  useEffect(() => {
    const targetMode = loadFullData ? 'full' : 'light';

    // Skip if we've already fetched enough data for the current mode.
    // A previous 'full' fetch satisfies a 'light' consumer too, so we only
    // need to re-fetch when upgrading to 'full'.
    if (!effectiveVendorId) return;
    if (loadedModeRef.current === 'full') return;
    if (loadedModeRef.current === targetMode) return;

    loadedModeRef.current = targetMode;

    apolloClient
      .query<ViewProcessorAdminQuery | ViewProcessorAdminLightQuery>({
        query: queryDocument,
        fetchPolicy: 'network-only',
        ...(queryVariables && { variables: queryVariables }),
      })
      .then((result) => {
        if (result.data) {
          freshDataRef.current = JSON.parse(JSON.stringify(result.data));
        }
      })
      .catch(() => {
        // Silently handle error — the query hook will surface the error state.
        // Reset so a future mount can retry.
        loadedModeRef.current = 'none';
      });
    // Re-run if loadFullData or vendorId changes so we refetch for the new scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveVendorId, loadFullData, vendorId]);

  // Manual refetch writes to cache, which triggers the cache-only subscription.
  const refetch = useCallback(
    () =>
      apolloClient
        .query<ViewProcessorAdminQuery | ViewProcessorAdminLightQuery>({
          query: queryDocument,
          fetchPolicy: 'network-only',
          ...(queryVariables && { variables: queryVariables }),
        })
        .then((result) => {
          freshDataRef.current = JSON.parse(JSON.stringify(result.data));
          return result;
        }),
    // queryDocument is derived from loadFullData; queryVariables is memoized on vendorId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apolloClient, queryDocument, queryVariables],
  );

  const contextValue: SettingsContextValue = useMemo(
    () => ({
      settings: {
        ...queryResult,
        // Use freshData as fallback if query data is undefined.
        // This ensures we always have data even if the cache-only query loses it
        // (e.g. after a cache eviction or a full resetStore call).
        data: queryResult.data || freshDataRef.current,
      } as Maybe<
        ViewProcessorAdminQueryResult | ViewProcessorAdminLightQueryResult
      >,
      refetch,
      freshData: freshDataRef.current,
      isFullDataLoaded: loadFullData,
    }),
    // loadFullData is stable per render so it's safe to include.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryResult, refetch, loadFullData],
  );

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  );
}

/**
 * Hook to access processor settings from the SettingsProvider.
 * Provides settings query result, refetch method, and fresh data reference.
 * @throws Error if used outside of SettingsProvider
 * @returns Settings context value containing settings, refetch method, and freshData
 * @example
 * ```tsx
 * const { settings, refetch, freshData } = useSettings();
 *
 * // Access settings data
 * const processorSettings = settings.data?.processorSettingsOne;
 *
 * // Refetch settings
 * await refetch();
 * ```
 */
export function useSettings(): SettingsContextValue {
  const context = useContext(SettingsContext);
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
